import React, { useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';
import SessionController from '../../components/teacher/SessionController';
import { api, useQuery, useMutation } from '../../lib/convex';
import type { Id } from '../../lib/convex';
import { TeacherAuth } from '../../components/auth/TeacherAuth';
import QRCode from 'react-qr-code';

const SessionControlPage: React.FC = () => {
  return (
    <TeacherAuth>
      <SessionControlContent />
    </TeacherAuth>
  );
};

const SessionControlContent: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams({ from: '/teacher/sessions/$sessionId' });
  
  const session = useQuery(api.sessions.get, { 
    id: sessionId as Id<"sessions">
  });
  
  const quizId = session?.quizId;
  const quiz = useQuery(
    api.quizzes.get, 
    quizId !== undefined ? { id: quizId as Id<"quizzes"> } : "skip"
  );
  
  const participants = useQuery(api.sessions.getParticipants, {
    id: sessionId as Id<"sessions">
  }) || [];

  const startSession = useMutation(api.sessions.start);
  const nextQuestion = useMutation(api.sessions.nextQuestion);
  const previousQuestion = useMutation(api.sessions.previousQuestion);
  const endSession = useMutation(api.sessions.end);

  // Create join URL with session code
  const joinUrl = useMemo(() => {
    if (!session?.code) return '';
    return `${window.location.origin}/join?code=${session.code}`;
  }, [session?.code]);

  const handleStartSession = async () => {
    try {
      await startSession({ id: sessionId as Id<"sessions"> });
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    }
  };

  const handleNextQuestion = async () => {
    try {
      await nextQuestion({ id: sessionId as Id<"sessions"> });
    } catch (error) {
      console.error('Error advancing to next question:', error);
      alert('Failed to advance to next question. Please try again.');
    }
  };

  const handlePreviousQuestion = async () => {
    try {
      await previousQuestion({ id: sessionId as Id<"sessions"> });
    } catch (error) {
      console.error('Error going back to previous question:', error);
      alert('Failed to go back to previous question. Please try again.');
    }
  };

  const handleEndSession = async () => {
    if (confirm('Are you sure you want to end this session?')) {
      try {
        await endSession({ id: sessionId as Id<"sessions"> });
        navigate({ to: '/teacher' });
      } catch (error) {
        console.error('Error ending session:', error);
        alert('Failed to end session. Please try again.');
      }
    }
  };

  if (!session || !quiz) {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading session data...</p>
        </div>
      </div>
    );
  }

  if (session.status === 'waiting') {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Waiting to Start: {quiz.title}</CardTitle>
            <CardDescription>
              Share the code below with students to join
            </CardDescription>
          </CardHeader>
          
          <CardContent className="text-center py-12">
            <div className="mb-8">
              <p className="text-sm text-muted-foreground mb-2">Session Code</p>
              <p className="text-6xl font-bold tracking-wider text-primary mb-4">
                {session.code}
              </p>
              
              <div className="flex justify-center mt-6 mb-2">
                <div className="p-4 bg-white rounded-lg">
                  {joinUrl && (
                    <QRCode 
                      value={joinUrl}
                      size={180}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                Scan to join the session
              </p>
            </div>
            
            <div className="mb-8">
              <p className="text-lg font-medium mb-4">
                Participants: {participants.length}
              </p>
              
              {participants.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
                  {participants.map((p) => (
                    <span 
                      key={p._id} 
                      className="px-3 py-1 bg-secondary rounded-full text-sm"
                    >
                      {p.nickname}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <Button 
              size="lg"
              onClick={handleStartSession}
              disabled={participants.length === 0}
            >
              Start Quiz
            </Button>
            
            {participants.length === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">
                Waiting for participants to join...
              </p>
            )}
          </CardContent>
          
          <CardFooter className="justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate({ to: '/teacher' })}
            >
              Back to Dashboard
            </Button>
            <Button 
              variant="destructive"
              onClick={handleEndSession}
            >
              Cancel Session
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Session Control</h1>
        <Button variant="outline" onClick={() => navigate({ to: '/teacher' })}>
          Back to Dashboard
        </Button>
      </div>

      <div className="mb-4 p-3 bg-secondary rounded-md flex justify-between items-center">
        <div>
          <p className="font-medium">
            Quiz: <span className="font-bold">{quiz.title}</span>
          </p>
          <p>
            Session Code: <span className="font-bold text-primary">{session.code}</span>
          </p>
          <div className="flex items-center mt-2">
            <div className="p-2 bg-white rounded-md mr-3">
              {joinUrl && (
                <QRCode 
                  value={joinUrl}
                  size={80}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Scan to join
            </p>
          </div>
        </div>
        <div>
          <p>Status: <span className="font-medium">{session.status}</span></p>
          <p>Participants: <span className="font-medium">{participants.length}</span></p>
        </div>
      </div>

      <SessionController
        session={session}
        quiz={quiz}
        participants={participants}
        onNextQuestion={handleNextQuestion}
        onPreviousQuestion={handlePreviousQuestion}
        onEndSession={handleEndSession}
      />
    </div>
  );
};

export default SessionControlPage;